import { ctxTachograph } from '@/contexts/TachographCtx';
import { CheckCircleOutlineOutlined, Email, ErrorOutline } from '@mui/icons-material';
import { Button, Drawer, Fab, FilledInput, FormControl, InputAdornment, InputLabel, Typography } from '@mui/material';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TachographFileNameSelector from './TachographFileNameSelector';
import { TachoTabs } from './TachographPopup';
import { useNotificationStore } from '@/stores/notificationstore';
import axios, { AxiosResponse } from 'axios';

export default function TachographEmailButton({ activeTab }: { activeTab?: TachoTabs }) {
  const tachographCtx = useContext(ctxTachograph);
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const showNotification = useNotificationStore((state) => state.showNotification);

  if (!tachographCtx) return;

  function resultNotifier(result: boolean) {
    setOpen(false);

    if (activeTab === 'TargetTab') {
      tachographCtx?.setSelectedRowIds([]);
    } else {
      tachographCtx?.setSelectedRowCardNumbers([]);
    }

    if (result) {
      showNotification('info', t('tachograph.successfulEmail'), () => {}, 5);
    } else {
      showNotification('error', t('tachograph.errorEmail'), () => {}, 5);
    }
  }

  async function getSentEmailStatus(): Promise<AxiosResponse<boolean> | undefined> {
    if (
      !tachographCtx ||
      !tachographCtx.addressee ||
      !tachographCtx.selectedDates ||
      !tachographCtx.selectedDates.from ||
      !tachographCtx.selectedDates.to ||
      (tachographCtx.selectedRowIds.length == 0 && tachographCtx.selectedRowCardNumbers.length == 0)
    ) {
      return undefined;
    }

    const params = new URLSearchParams();
    let url;

    params.append('isCroatianFileName', tachographCtx.isCroatian.toString());
    params.append('addressee', tachographCtx.addressee);
    params.append('fromTimeMillis', tachographCtx.selectedDates.from.getTime().toString());
    params.append('toTimeMillis', tachographCtx.selectedDates.to.getTime().toString());

    if (activeTab === 'TargetTab') {
      tachographCtx.selectedRowIds.forEach((id) => params.append('id', id.toString()));

      url = `/api/v1.1/tachograph/sendVehicleDataToAddress?${params.toString()}`;
    } else {
      tachographCtx.selectedRowCardNumbers.forEach((driverCardNumber) => {
        const onlyCardNumber = driverCardNumber.toString().split('_')[1];
        params.append('driverCardNumbers', onlyCardNumber);
      });

      url = `/api/v1.1/tachograph/sendDriverDataToAddress?${params.toString()}`;
    }

    return axios.get<boolean>(url);
  }

  async function onAction() {
    setLoading(true);

    try {
      const result = await getSentEmailStatus();

      if (result) {
        resultNotifier(result.data);
      }
    } catch (err) {
      console.error('Error :', err);
    }
  }

  function validateEmail(value: string): boolean {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(value);
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const newValue = e.target.value;
    tachographCtx?.setAddressee(newValue);
    setError(newValue !== '' && !validateEmail(newValue));
  }

  function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
    const newValue = e.target.value;
    tachographCtx?.setAddressee(newValue);
    setError(newValue !== '' && !validateEmail(newValue));
  }
  return (
    <>
      <Fab
        variant="extended"
        color="secondary"
        onClick={() => setOpen(true)}
        sx={{
          position: 'fixed',
          bottom: 80,
          right: 16,
          zIndex: 1200,
          boxShadow: '0px 6px 16px rgba(0, 0, 0, 0.3)',
          color: 'white',
        }}
      >
        <Email sx={{ mr: 1 }} />
        {t('tachograph.downloadButton.send')}
      </Fab>

      <Drawer
        anchor="bottom"
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        slotProps={{
          root: { sx: { zIndex: 1300 } },
          paper: {
            sx: {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              paddingBottom: '60px',
              overflowY: 'auto',
            },
          },
        }}
      >
        <Typography
          variant="subtitle1"
          gutterBottom
          fontWeight={500}
          sx={{
            padding: '20px',
            borderBottom: '2px solid',
            borderColor: 'divider',
            display: 'inline-block',
            pb: 0.5,
            mb: 1,
          }}
        >
          {t('tachograph.downloadButton.drawerTitle')}
        </Typography>

        <div style={{ padding: '16px 8px 16px 8px' }}>
          <div style={{ marginLeft: '16px' }}>
            <TachographFileNameSelector isBottom={true} />
          </div>

          <FormControl variant="filled" margin="normal" fullWidth autoFocus error={error}>
            <InputLabel sx={{ color: 'grey' }} htmlFor="email" style={{ color: 'grey' }} shrink={true}>
              {t('tachograph.email.shrinked')}
            </InputLabel>

            <FilledInput
              id="email"
              name="email"
              type="email"
              onChange={handleChange}
              onBlur={handleBlur}
              defaultValue={tachographCtx.addressee}
              placeholder={t('tachograph.email.placeHolder')}
              data-qa-element-id="email"
              sx={{
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'transparent',
                },
                '&.Mui-focused': {
                  backgroundColor: 'transparent',
                },
                // text color
                color: 'black',

                '&::placeholder': {
                  color: 'grey.500',
                  opacity: 1,
                },

                '&:before, &:after': {
                  borderBottom: '1px solid grey',
                },
                '&:hover:not(.Mui-disabled, .Mui-error):before': {
                  borderBottom: '1px solid black',
                },
              }}
              endAdornment={
                error ? (
                  <InputAdornment position="end" sx={{ color: '#d32f2f' }}>
                    <ErrorOutline fontSize="small" />
                  </InputAdornment>
                ) : (
                  !!tachographCtx.addressee && (
                    <InputAdornment position="end" sx={{ color: 'green' }}>
                      <CheckCircleOutlineOutlined fontSize="small" />
                    </InputAdornment>
                  )
                )
              }
            />
          </FormControl>

          <Button
            variant="contained"
            color="secondary"
            onClick={onAction}
            disabled={loading || tachographCtx.addressee === '' || error}
            fullWidth
            sx={{
              mt: 2,
              width: '60%',
              ml: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              fontSize: '14px',
              padding: '6px 12px',
            }}
          >
            <Email />
            {tachographCtx?.isCroatian
              ? t('tachograph.downloadButton.sendHrFile')
              : t('tachograph.downloadButton.sendGeneralFile')}
          </Button>
        </div>
      </Drawer>
    </>
  );
}
